// components/account/AccountHistoricsModal/components/HistoricsTable.jsx
import { Table, Badge, Alert } from 'react-bootstrap';
import { 
  FaFile,
  FaCalendar,
  FaInfoCircle,
  FaMoneyBillWave,
  FaFilter
} from 'react-icons/fa';
import { usePagination } from '../../service/Hooks/AccountHistorics/usePagination';
import PaginationControls from './PaginationControls';
import { formatAmount } from '../../service/utils/transactionUtils';
import { formatDate } from '../../service/utils/formatters';


/**
 * Tableau des historiques avec pagination front-end
 */
const HistoricsTable = ({ 
  historics = [], 
  onViewDocument,
  pageSize: initialPageSize = 15 
}) => {
  // Utilisation du hook de pagination
  const {
    currentPage,
    pageSize,
    totalPages,
    paginatedData,
    totalItems,
    startIndex,
    endIndex,
    hasNextPage,
    hasPreviousPage,
    goToPage,
    nextPage,
    previousPage,
    firstPage,
    lastPage,
    changePageSize
  } = usePagination(historics, { pageSize: initialPageSize });

  /**
   * Détermine la couleur du badge selon la catégorie
   */
  const getCategoryBadge = (categorie) => {
    const categoryColors = {
      'Security': 'primary bg-opacity-10 border-primary text-primary',
      'Cash': 'bg-success bg-opacity-10 border-success text-success',
      'Depot': 'bg-info bg-opacity-10 border-info text-info',
      'Commission Trimestrielle': 'bg-warning bg-opacity-10 border-warning text-warning',
      'Retrait': 'bg-danger bg-opacity-10 border-danger text-danger',
      'Dividende': 'bg-secondary bg-opacity-10 border-secondary text-secondary'
    };
    
    return categoryColors[categorie] || 'dark';
  };

  /**
   * Formate le montant avec couleur selon le signe
   */
  const formatAmountWithColor = (amount) => {
    const formatted = formatAmount(amount);
    const colorClass = amount >= 0 ? 'text-success' : 'text-danger';
    return <span className={colorClass}>{formatted}</span>;
  };

  /**
   * Formate la quantité avec couleur selon le signe
   */
  const formatQuantityWithColor = (quantity) => {
    const colorClass = quantity >= 0 ? 'text-success' : 'text-danger';
    const sign = quantity >= 0 ? '+' : '';
    return <span className={colorClass}>{sign}{quantity}</span>;
  };

  /**
   * Calcule les totaux pour la page courante
   */
  const calculatePageTotals = () => {
    return {
      quantity: paginatedData.reduce((sum, h) => sum + h.quantity, 0),
      value: paginatedData.reduce((sum, h) => sum + h.lastValue, 0),
      documents: paginatedData.filter(h => h.hasDocument).length
    };
  };

  /**
   * Calcule les totaux globaux
   */
  const calculateGlobalTotals = () => {
    return {
      quantity: historics.reduce((sum, h) => sum + h.quantity, 0),
      value: historics.reduce((sum, h) => sum + h.lastValue, 0),
      documents: historics.filter(h => h.hasDocument).length
    };
  };

  const pageTotals = calculatePageTotals();
  const globalTotals = calculateGlobalTotals();

  if (historics.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="bg-light rounded-3 px-4 py-5 mx-auto" style={{ maxWidth: "400px" }}>
          <FaInfoCircle className="text-muted fa-3x mb-3" />
          <h5 className="fw-semibold mb-2">Aucun historique trouvé</h5>
          <p className="text-muted mb-0">
            Aucun résultat pour les filtres sélectionnés
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card border">
      {/* En-tête avec statistiques */}
      <div className="card-header bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <FaCalendar className="me-2" />
            Liste des historiques ({totalItems})
          </h6>
          <div className="d-flex gap-2">
            <Badge bg="light" text="dark" className="d-flex align-items-center">
              <FaFilter className="me-1" size={12} />
              Page {currentPage}/{totalPages}
            </Badge>
            <Badge bg="info" className="d-flex align-items-center">
              {pageSize} / page
            </Badge>
          </div>
        </div>
      </div>
      
      {/* Contrôles de pagination en haut */}
      <div className="card-body border-bottom py-3">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          hasPreviousPage={hasPreviousPage}
          hasNextPage={hasNextPage}
          goToPage={goToPage}
          nextPage={nextPage}
          previousPage={previousPage}
          firstPage={firstPage}
          lastPage={lastPage}
          changePageSize={changePageSize}
          pageSizeOptions={[15, 30, 50, 100]}
        />
      </div>

      {/* Tableau des données */}
      <div className="card-body p-0">
        <div className="table-responsive">
          <Table hover className="mb-0 table-striped">
            <thead>
              <tr className="bg-light">
                <th>Date</th>
                <th>Catégorie</th>
                <th>Description</th>
                <th className="text-end">Quantité</th>
                <th className="text-end">Valeur</th>
                <th className="text-center">Document</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((historic) => (
                <tr key={historic.id} className="border-bottom">
                  {/* Date */}
                  <td>
                    <div className="d-flex align-items-center">
                      <FaCalendar className="text-muted me-2" size={12} />
                      <span className="text-nowrap">
                        {formatDate(historic.date)}
                      </span>
                    </div>
                  </td>

                  {/* Catégorie */}
                  <td>
                    <Badge 
                      bg={`px-3 py-2 ${getCategoryBadge(historic.categorie)}`}
                    >
                      {historic.categorie}
                    </Badge>
                  </td>

                  {/* Description */}
                  <td>
                    <div className="fw-medium">{historic.descriptif}</div>
                    <small className="text-muted">{historic.name}</small>
                  </td>

                  {/* Quantité */}
                  <td className="text-end">
                    <div className="fw-bold">
                      {formatQuantityWithColor(historic.quantity)}
                    </div>
                  </td>

                  {/* Valeur */}
                  <td className="text-end">
                    <div className="d-flex align-items-center justify-content-end">
                      <FaMoneyBillWave className="text-muted me-2" size={12} />
                      <div className="fw-bold">
                        {formatAmountWithColor(historic.lastValue)}
                      </div>
                    </div>
                  </td>

                  {/* Document */}
                  <td className="text-center">
                    <div className="d-flex justify-content-center">
                      {historic.hasDocument ? (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => onViewDocument && onViewDocument(historic)}
                          title="Voir le document"
                        >
                          <FaFile />
                        </button>
                      ) : (
                        <span 
                          className="badge bg-secondary bg-opacity-25 text-secondary"
                          title="Aucun document disponible"
                        >
                          <FaFile />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Résumé de la page */}
      <div className="card-body border-top py-3 bg-light">
        <div className="row">
          <div className="col-md-4">
            <small className="text-muted d-block">Totaux de la page</small>
            <div className="fw-medium">
              Quantité: <span className={pageTotals.quantity >= 0 ? 'text-success' : 'text-danger'}>
                {formatQuantityWithColor(pageTotals.quantity)}
              </span>
            </div>
            <div className="fw-medium">
              Valeur: <span className={pageTotals.value >= 0 ? 'text-success' : 'text-danger'}>
                {formatAmountWithColor(pageTotals.value)}
              </span>
            </div>
            <div className="fw-medium">
              Documents: <Badge bg="info">{pageTotals.documents}</Badge>
            </div>
          </div>
          
          <div className="col-md-4 text-center">
            <small className="text-muted d-block">Informations</small>
            <div className="fw-medium">
              Historiques: {paginatedData.length} sur {totalItems}
            </div>
            <div className="fw-medium">
              Page: {currentPage} sur {totalPages}
            </div>
            <div className="fw-medium">
              Taille: {pageSize} / page
            </div>
          </div>
          
          <div className="col-md-4 text-end">
            <small className="text-muted d-block">Totaux globaux</small>
            <div className="fw-medium">
              Quantité totale: <span className={globalTotals.quantity >= 0 ? 'text-success' : 'text-danger'}>
                {formatQuantityWithColor(globalTotals.quantity)}
              </span>
            </div>
            <div className="fw-medium">
              Valeur totale: <span className={globalTotals.value >= 0 ? 'text-success' : 'text-danger'}>
                {formatAmountWithColor(globalTotals.value)}
              </span>
            </div>
            <div className="fw-medium">
              Documents totaux: <Badge bg="success">{globalTotals.documents}</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Contrôles de pagination en bas */}
      <div className="card-footer bg-light">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          hasPreviousPage={hasPreviousPage}
          hasNextPage={hasNextPage}
          goToPage={goToPage}
          nextPage={nextPage}
          previousPage={previousPage}
          firstPage={firstPage}
          lastPage={lastPage}
          changePageSize={changePageSize}
          pageSizeOptions={[15, 30, 50, 100]}
        />
      </div>
    </div>
  );
};

export default HistoricsTable;